import Modal from '@atlaskit/modal-dialog';
import Form from '../../../../kingpin/forms/Form';
import FormHeader from '../../../../kingpin/forms/FormHeader';
import FormContent from '../../../../kingpin/forms/FormContent';
import { useCallback, useContext, useMemo } from 'react';
import { V3AppsContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appNavItemTypeCheckers from '../../../../types/appNavItemTypeCheckers';
import styled from 'styled-components';
import Typography from '../../../../kingpin/atoms/Typography';
import Row from '../../../../components/Common/Row';
import { Link } from 'react-router-dom';

const ItemDivInner = styled.div`
  display: flex;
  height: 40px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  user-select: none;
  flex: 1;
`;

const ItemDivOuter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 8px 0px 0px;
  cursor: pointer;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-border-secondary);
  }

  &:hover {
    opacity: 0.7;
    background-color: var(--color-bg-hover);
  }
`;

const ContentItem = ({ link, name }: { link: string; name: string }) => {
  return (
    <ItemDivOuter>
      <ItemDivInner>
        <Row centerAlign>
          <Link to={link}>
            <Typography.Body type={'Body 13'} isLink>
              {name}
            </Typography.Body>
          </Link>
        </Row>
      </ItemDivInner>
    </ItemDivOuter>
  );
};

const UsedInApps = ({ apps }: { apps: V3.ProductApp[] }) => {
  const { availableProductApps, allProducts } = useContext(V3AppsContext);
  const getLink = useCallback(
    (app: V3.ProductApp) => {
      const selectedProduct = allProducts.find((p) =>
        p.appIds.includes(app.id),
      );
      if (!selectedProduct) {
        return '';
      }

      const subAppsThisUserHasAccessTo = availableProductApps.filter((app) =>
        selectedProduct.appIds.includes(app.id),
      );

      if (subAppsThisUserHasAccessTo.length === 0) {
        return '/';
      }

      if (subAppsThisUserHasAccessTo.length === 1) {
        return `/apps/${selectedProduct.urlSlug}`;
      }

      return `/apps/${selectedProduct.urlSlug}/${app.urlSlug}`;
    },
    [allProducts, availableProductApps],
  );

  if (apps.length === 0) {
    return null;
  }

  return (
    <div>
      <Typography.Header type={'H5'}>Product Apps</Typography.Header>
      {apps.map((app) => (
        <ContentItem name={app.label} link={getLink(app)} key={app.id} />
      ))}
    </div>
  );
};

const UsedInWorkspaces = ({ workspaces }: { workspaces: V3.Workspace[] }) => {
  const getLink = useCallback((workspace: V3.Workspace) => {
    return `/workspaces/${workspace.urlSlug}`;
  }, []);

  if (workspaces.length === 0) {
    return null;
  }

  return (
    <div>
      <Typography.Header type={'H5'}>Product Apps</Typography.Header>
      {workspaces.map((workspace) => (
        <ContentItem
          name={workspace.label}
          link={getLink(workspace)}
          key={workspace.id}
        />
      ))}
    </div>
  );
};

const EntityAppUsage = ({
  entityApp,
  close,
}: {
  entityApp: EntityDetails.App;
  close: () => void;
}) => {
  const { allProductApps, allWorkspaces } = useContext(V3AppsContext);
  const getIsInNavSettings = useCallback(
    (settings: V3.Navigation.Config) => {
      return settings.sections.some((section) => {
        return section.items.some((item) => {
          const isMappedApp =
            appNavItemTypeCheckers.isMappedEntityView(item) &&
            item.entityAppId === entityApp.id;

          const isEntityApp =
            appNavItemTypeCheckers.isEntityApp(item) &&
            item.entityAppId === entityApp.id;

          return isMappedApp || isEntityApp;
        });
      });
    },
    [entityApp.id],
  );

  const usageReport = useMemo((): {
    apps: V3.ProductApp[];
    workspaces: V3.Workspace[];
  } => {
    return {
      apps: allProductApps.filter((app) =>
        getIsInNavSettings(app.navigationSettings),
      ),
      workspaces: allWorkspaces.filter((workspace) =>
        getIsInNavSettings(workspace.navigationSettings),
      ),
    };
  }, [allProductApps, allWorkspaces, getIsInNavSettings]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader title={`"${entityApp.title}" Usage`} onClose={close} />
        <FormContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <UsedInApps apps={usageReport.apps} />
            <UsedInWorkspaces workspaces={usageReport.workspaces} />
          </div>
        </FormContent>
      </Form>
    </Modal>
  );
};

export default EntityAppUsage;
